import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatFrameType } from '../Pages/AgentHelpers';
import { AssistantFrameApi } from '../Services/AgentApiProviderTypes';
import Button from './Buttons';
import ItemsPickerModal from './ItemsPickerModal';
import { FetchItemsParams, FetchItemsResult, PickerColumn } from './ItemsPickerModalTypes';

const frameColumns: PickerColumn<AssistantFrameApi>[] = [
  {
    key: 'id',
    header: 'ID',
    widthClass: 'items-picker-col--flex-2',
    render: (item) => (
      <div className="items-picker-id small-font" title={item.id}>
        {item.id}
      </div>
    ),
  },
  {
    key: 'name',
    header: 'Name',
    widthClass: 'items-picker-col--flex-3',
    render: (item) => (
      <div className="items-picker-name" title={item.name || ''}>
        {item.name}
      </div>
    ),
  },
  {
    key: 'type',
    header: 'Type',
    widthClass: 'items-picker-col--flex-2',
    render: (item) => (
      <div className="items-picker-description small-font">{formatFrameType(item.frame_type)}</div>
    ),
  },
];

interface AgentFramesPickerProps {
  // Selected frame ids — the single source of truth, owned by the parent form.
  value: string[];
  onChange: (frameIds: string[]) => void;
  // Full objects for frames already linked to the agent, so the summary can
  // show names and the modal can keep the selection without loading its page.
  selectedFrames: AssistantFrameApi[];
  fetchFrames: (params: FetchItemsParams) => Promise<FetchItemsResult<AssistantFrameApi>>;
  disabled?: boolean;
}

function AgentFramesPicker({
  value,
  onChange,
  selectedFrames,
  fetchFrames,
  disabled = false,
}: AgentFramesPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  // Frames picked during this session are remembered so the summary can name
  // them without waiting for the agent to be saved and reloaded.
  const [knownFrames, setKnownFrames] = useState<Record<string, AssistantFrameApi>>({});

  useEffect(() => {
    if (selectedFrames.length === 0) return;
    setKnownFrames((prev) => {
      const next = { ...prev };
      for (const frame of selectedFrames) next[frame.id] = frame;
      return next;
    });
  }, [selectedFrames]);

  useEffect(() => {
    if (value.length === 0 || totalCount !== null) return;
    let cancelled = false;
    fetchFrames({ limit: 1, pageToken: null, searchQuery: null })
      .then((result) => {
        if (!cancelled && result.totalCount != null) {
          setTotalCount(result.totalCount);
        }
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [value.length, totalCount, fetchFrames]);

  const selectedItems = useMemo(
    () => value.map((id) => knownFrames[id]).filter(Boolean),
    [value, knownFrames],
  );

  const summary = useMemo(() => {
    if (value.length === 0) return 'No frames selected';
    if (totalCount !== null) return `${value.length} selected of ${totalCount}`;
    return `${value.length} selected`;
  }, [value, totalCount]);

  const handleSaveSelected = useCallback(
    (picked: AssistantFrameApi[]) => {
      setKnownFrames((prev) => {
        const next = { ...prev };
        for (const frame of picked) next[frame.id] = frame;
        return next;
      });
      onChange(picked.map((frame) => frame.id));
      setIsOpen(false);
    },
    [onChange],
  );

  return (
    <>
      <div className="align-wrapper d-flex align-center">
        <div className="small-font" style={{ marginRight: 12, textAlign: 'right' }} title={summary}>
          {summary}
        </div>
        <Button variant="primary" size="medium" disabled={disabled} onClick={() => setIsOpen(true)}>
          Select frames
        </Button>
      </div>

      <ItemsPickerModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Agent Frames"
        disabled={disabled}
        fetchItems={fetchFrames}
        columns={frameColumns}
        multiSelect
        selectedIds={value}
        selectedItems={selectedItems}
        onSaveSelected={handleSaveSelected}
        saveLabel="Save selected"
        allowEmptySave
        searchPlaceholder="Search frames by name..."
      />
    </>
  );
}

export default AgentFramesPicker;
